import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useApi } from '../../../hooks/useApi';

function MediaServersSection() {
  const api = useApi();
  const navigate = useNavigate();
  const [detailServer, setDetailServer] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-media-servers'],
    queryFn: () => api.get('/api/admin/media-servers'),
    refetchInterval: 15000,
  });

  if (isLoading) return <CircularProgress size={24} />;
  if (error) return <Alert severity="error">{error.message}</Alert>;

  const servers = Array.isArray(data) ? data : [];

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Instance ID</TableCell>
              <TableCell>Rooms (DB)</TableCell>
              <TableCell>Rooms (Server)</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>Region</TableCell>
              <TableCell>State</TableCell>
              <TableCell>CPU %</TableCell>
              <TableCell>Memory %</TableCell>
              <TableCell>Load</TableCell>
              <TableCell>Scale</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {servers.map((s, i) => (
              <TableRow
                key={`${s.instanceId}-${s.region || i}`}
                hover
                onClick={() => setDetailServer(s)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  {s.instanceId || '—'}
                </TableCell>
                <TableCell>{s.instanceRoomCount ?? '—'}</TableCell>
                <TableCell>{s.actualRoomCount ?? '—'}</TableCell>
                <TableCell>{s.ipAddress || '—'}</TableCell>
                <TableCell>{s.region || '—'}</TableCell>
                <TableCell>
                  <Chip label={s.state || 'unknown'} size="small" color={s.state === 'running' ? 'success' : 'default'} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color={s.serverLoad?.cpuUsagePercent > 80 ? 'error.main' : 'inherit'}>
                    {s.serverLoad?.cpuUsagePercent != null ? `${Math.round(s.serverLoad.cpuUsagePercent)}%` : '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color={s.serverLoad?.memoryUsagePercent > 80 ? 'error.main' : 'inherit'}>
                    {s.serverLoad?.memoryUsagePercent != null ? `${Math.round(s.serverLoad.memoryUsagePercent)}%` : '—'}
                  </Typography>
                </TableCell>
                <TableCell>{s.serverLoad?.loadAverage ?? '—'}</TableCell>
                <TableCell>
                  {s.serverLoad?.shouldScale != null ? (
                    <Chip label={s.serverLoad.shouldScale ? 'Yes' : 'No'} size="small" color={s.serverLoad.shouldScale ? 'warning' : 'default'} />
                  ) : '—'}
                </TableCell>
              </TableRow>
            ))}
            {servers.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">No media servers found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Detail Dialog */}
      <Dialog open={!!detailServer} onClose={() => setDetailServer(null)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Media Server: {detailServer?.instanceId}
        </DialogTitle>
        <DialogContent>
          {detailServer?.simpleStats?.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Room</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>Transports</TableCell>
                    <TableCell>Producers</TableCell>
                    <TableCell>Consumers</TableCell>
                    <TableCell>Users</TableCell>
                    <TableCell>Running (min)</TableCell>
                    <TableCell>API Key</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailServer.simpleStats.map((room) => {
                    const runningMin = room.created
                      ? Math.round((Date.now() - new Date(room.created).getTime()) / 60000)
                      : null;
                    return (
                      <TableRow
                        key={room.roomId || room.roomName}
                        hover
                        onDoubleClick={() => {
                          setDetailServer(null);
                          if (room.roomId) navigate(`/rooms/${room.roomId}`);
                        }}
                        sx={{ cursor: room.roomId ? 'pointer' : 'default' }}
                      >
                        <TableCell>{room.roomName || room.roomId}</TableCell>
                        <TableCell>{room.owner || '—'}</TableCell>
                        <TableCell>{room.transportCount ?? '—'}</TableCell>
                        <TableCell>{room.producerCount ?? '—'}</TableCell>
                        <TableCell>{room.consumerCount ?? '—'}</TableCell>
                        <TableCell>{Array.isArray(room.users) ? room.users.length : '—'}</TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color={runningMin > 150 ? 'error.main' : 'inherit'}
                          >
                            {runningMin ?? '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                          {room.apiKey || '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography sx={{
              color: "text.secondary"
            }}>No rooms on this server</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailServer(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function SignalingServersSection() {
  const api = useApi();
  const [regionFilter, setRegionFilter] = useState(() => {
    try { return localStorage.getItem('admin_signaling_region') || ''; } catch { return ''; }
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-signaling-servers'],
    queryFn: () => api.get('/api/admin/signaling-servers'),
  });

  const servers = Array.isArray(data) ? data : [];
  const regions = [...new Set(servers.map((s) => s.region).filter(Boolean))];
  const filtered = regionFilter ? servers.filter((s) => s.region === regionFilter) : servers;

  const handleRegionChange = (e) => {
    const val = e.target.value;
    setRegionFilter(val);
    try { localStorage.setItem('admin_signaling_region', val); } catch { /* ignore */ }
  };

  const formatUptime = (seconds) => {
    if (seconds == null) return '—';
    return `${Math.round(seconds / 60)}m`;
  };

  const toMB = (bytes) => {
    if (bytes == null) return '—';
    return (bytes / (1024 * 1024)).toFixed(1);
  };

  if (isLoading) return <CircularProgress size={24} />;
  if (error) return <Alert severity="error">{error.message}</Alert>;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          select
          label="Region"
          value={regionFilter}
          onChange={handleRegionChange}
          size="small"
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All Regions</MenuItem>
          {regions.map((r) => (
            <MenuItem key={r} value={r}>{r}</MenuItem>
          ))}
        </TextField>
        <Button startIcon={<RefreshIcon />} onClick={() => refetch()} size="small">
          Refresh
        </Button>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Instance ID</TableCell>
              <TableCell>Region</TableCell>
              <TableCell>Timestamp</TableCell>
              <TableCell>Uptime</TableCell>
              <TableCell>DB Health</TableCell>
              <TableCell>DB Connected</TableCell>
              <TableCell>Heap Used (MB)</TableCell>
              <TableCell>Heap Total (MB)</TableCell>
              <TableCell>Total Users</TableCell>
              <TableCell>Total Rooms</TableCell>
              <TableCell>Active Rooms</TableCell>
              <TableCell>Sockets</TableCell>
              <TableCell>Total Requests</TableCell>
              <TableCell>Errors</TableCell>
              <TableCell>Memory %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((s, i) => (
              <TableRow key={s.instanceId || s._id || i}>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  {s.instanceId || '—'}
                </TableCell>
                <TableCell>{s.region || '—'}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>
                  {s.timestamp ? new Date(s.timestamp).toLocaleString() : '—'}
                </TableCell>
                <TableCell>{formatUptime(s.system?.uptime)}</TableCell>
                <TableCell>
                  <Chip
                    label={s.database?.healthCheck?.status || 'unknown'}
                    size="small"
                    color={s.database?.healthCheck?.status === 'ok' ? 'success' : 'error'}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={s.database?.connection?.connected ? 'Yes' : 'No'}
                    size="small"
                    color={s.database?.connection?.connected ? 'success' : 'error'}
                  />
                </TableCell>
                <TableCell>{toMB(s.system?.memory?.heapUsed)}</TableCell>
                <TableCell>{toMB(s.system?.memory?.heapTotal)}</TableCell>
                <TableCell>{s.application?.attendees?.totalUsers ?? '—'}</TableCell>
                <TableCell>{s.application?.attendees?.totalRooms ?? '—'}</TableCell>
                <TableCell>{s.application?.activeRooms ?? '—'}</TableCell>
                <TableCell>{s.application?.socketCount ?? '—'}</TableCell>
                <TableCell>{s.application?.totalRequests ?? '—'}</TableCell>
                <TableCell>
                  <Typography variant="body2" color={s.application?.errorCount > 0 ? 'error.main' : 'inherit'}>
                    {s.application?.errorCount ?? '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color={s.application?.attendees?.memoryUsagePercent > 80 ? 'error.main' : 'inherit'}>
                    {s.application?.attendees?.memoryUsagePercent != null ? `${s.application.attendees.memoryUsagePercent}%` : '—'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={15} align="center">No signaling servers found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function AdminServerStatus() {
  const navigate = useNavigate();

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('/admin')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">Server Status</Typography>
      </Box>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Media Servers</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <MediaServersSection />
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Signaling Servers</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <SignalingServersSection />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default AdminServerStatus;
